

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { 
    getFirestore, 
    type Firestore,
    collection, 
    getDocs, 
    addDoc, 
    doc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where,
    writeBatch,
    Timestamp,
    setDoc,
    getDoc,
    documentId,
    orderBy,
    limit,
    runTransaction
} from 'firebase/firestore';
import { getAuth, type Auth } from 'firebase/auth';
import type { Order, RestaurantTable, MenuItem, MenuCategoryUIDefinition, RestaurantArea, TableStatus, CashUpSession, ShopInfoState, AppUser, UserRole, Store, DeliveryDetails, PaymentMethod, OrderItem } from '@/types';
import { createDefaultReceiptSettings, createDefaultShopInfoState } from '@/types';

// This is the primary configuration object for Firebase.
// It's defined here and used to initialize the app.
const firebaseConfig = {
  apiKey: "AIzaSyCMnfHx3IkLMVckYhjC8wEGGm5Og6Lpjrw",
  authDomain: "swiftserve-v2.firebaseapp.com",
  projectId: "swiftserve-v2",
  storageBucket: "swiftserve-v2.appspot.com",
  messagingSenderId: "859212736822",
  appId: "1:859212736822:web:48ddcb6cc44ec90a112b97",
};

// --- Firebase Initialization (Singleton Pattern) ---
// This pattern ensures that Firebase is initialized only once,
// preventing issues with hot-reloading and multiple initializations.
const app: FirebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const auth: Auth = getAuth(app);
const db: Firestore = getFirestore(app);


// Export the initialized instances for use throughout the app
export { app, db, auth };
const DB_UNAVAILABLE_ERROR = "Firestore is not initialized. Check your environment configuration.";

// --- Menu Items ---
export async function getMenuItems(storeId: string): Promise<MenuItem[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(collection(db, 'menuItems'), where('storeId', '==', storeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            name: data.name || '',
            price: data.price || 0,
            category: data.category || 'Uncategorized',
            imageUrl: data.imageUrl || null,
            dataAiHint: data.dataAiHint || null,
            description: data.description || null,
            calories: data.calories || null,
            allergenFlags: data.allergenFlags || {},
            modifiers: data.modifiers || [],
            notes: data.notes || null,
            storeId: data.storeId,
            isUpsellPromo: data.isUpsellPromo || false,
            upsellPromoDiscountPercent: data.upsellPromoDiscountPercent || null,
        } as MenuItem;
    });
}

export async function addMenuItem(itemData: Omit<MenuItem, 'id'>): Promise<MenuItem> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = await addDoc(collection(db, 'menuItems'), itemData);
    return { id: docRef.id, ...itemData };
}

export async function updateMenuItem(itemId: string, itemData: Partial<MenuItem>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const itemRef = doc(db, 'menuItems', itemId);
    await updateDoc(itemRef, itemData);
}

export async function deleteMenuItem(itemId: string): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    await deleteDoc(doc(db, 'menuItems', itemId));
}

// --- Categories ---
export async function getCategoriesUIDefinitions(storeId: string): Promise<MenuCategoryUIDefinition[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(collection(db, 'categories'), where('storeId', '==', storeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            name: data.name || '',
            colorHex: data.colorHex || '#CCCCCC',
            imageUrl: data.imageUrl || null,
            dataAiHint: data.dataAiHint || '',
            storeId: data.storeId,
        };
    });
}

export async function upsertCategoryUIDefinition(categoryData: MenuCategoryUIDefinition): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const catRef = doc(db, 'categories', categoryData.id as string);
    await setDoc(catRef, categoryData, { merge: true });
}


// --- Tables ---
export async function getRestaurantTables(storeId: string): Promise<RestaurantTable[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(collection(db, 'tables'), where('storeId', '==', storeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            name: data.name || '',
            capacity: data.capacity || 0,
            status: data.status || 'Available',
            currentOrderId: data.currentOrderId || null,
            currentOrderNumber: data.currentOrderNumber || null,
            currentOrderTotal: data.currentOrderTotal || null,
            currentOrderTimestamp: data.currentOrderTimestamp || null,
            storeId: data.storeId,
            notes: data.notes || null,
            areaId: data.areaId || null,
            x: data.x,
            y: data.y,
        } as RestaurantTable;
    });
}

export async function addRestaurantTable(tableData: Omit<RestaurantTable, 'id'>): Promise<RestaurantTable> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = await addDoc(collection(db, 'tables'), tableData);
    return { id: docRef.id, ...tableData };
}

export async function updateRestaurantTable(tableId: string, tableData: Partial<RestaurantTable>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const tableRef = doc(db, 'tables', tableId);
    await updateDoc(tableRef, tableData);
}

export async function deleteRestaurantTable(tableId: string): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    await deleteDoc(doc(db, 'tables', tableId));
}

export async function batchUpdateTableStatuses(tableIds: string[], status: TableStatus): Promise<void> {
    const currentDb = db;
    if (!currentDb) {
        throw new Error(DB_UNAVAILABLE_ERROR);
    }
    const batch = writeBatch(currentDb);
    tableIds.forEach(id => {
        const tableRef = doc(currentDb, 'tables', id);
        batch.update(tableRef, { status });
    });
    await batch.commit();
}


// --- Areas ---
export async function getRestaurantAreas(storeId: string): Promise<RestaurantArea[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(collection(db, 'areas'), where('storeId', '==', storeId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
            id: doc.id,
            name: data.name || '',
            storeId: data.storeId,
        };
    });
}

export async function addRestaurantArea(areaData: Omit<RestaurantArea, 'id'>): Promise<RestaurantArea> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = await addDoc(collection(db, 'areas'), areaData);
    return { id: docRef.id, ...areaData };
}

export async function updateRestaurantArea(areaId: string, areaData: Partial<RestaurantArea>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const areaRef = doc(db, 'areas', areaId);
    await updateDoc(areaRef, areaData);
}

export async function deleteAreaAndUnassignTables(areaId: string, tableIdsToUpdate: string[]): Promise<void> {
    const currentDb = db;
    if (!currentDb) {
        throw new Error(DB_UNAVAILABLE_ERROR);
    }
    const batch = writeBatch(currentDb);

    // Unassign tables
    tableIdsToUpdate.forEach(id => {
        const tableRef = doc(currentDb, 'tables', id);
        batch.update(tableRef, { areaId: null });
    });

    // Delete the area
    const areaRef = doc(currentDb, 'areas', areaId);
    batch.delete(areaRef);

    await batch.commit();
}


// --- Orders ---
const mapDocToOrder = (doc: any): Order => {
    const data = doc.data();
    return {
        id: doc.id,
        orderNumber: data.orderNumber || '',
        items: data.items || [],
        subtotal: data.subtotal || 0,
        tax: data.tax || 0,
        total: data.total || 0,
        status: data.status || 'pending',
        paymentMethod: data.paymentMethod || null,
        orderType: data.orderType || 'pos_takeaway',
        tableId: data.tableId || null,
        tableName: data.tableName || null,
        customerNotes: data.customerNotes || null,
        clientName: data.clientName || null,
        clientPhone: data.clientPhone || null,
        storeId: data.storeId,
        voidReason: data.voidReason || null,
        voidedBy: data.voidedBy || null,
        deliveryDetails: data.deliveryDetails || null,
        driver: data.driver || null,
        timestamp: (data.timestamp?.toMillis ? data.timestamp.toMillis() : data.timestamp) ?? 0,
        updatedAt: (data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt) ?? 0,
    };
};

export async function getNextOrderNumber(storeId: string): Promise<number> {
    const currentDb = db;
    if (!currentDb) {
        throw new Error(DB_UNAVAILABLE_ERROR);
    }
    const counterRef = doc(currentDb, 'storeCounters', storeId);
    const startOrderNumber = 1001;

    try {
        const newOrderNumber = await runTransaction(currentDb, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            const todayStr = new Date().toISOString().split('T')[0];

            if (!counterDoc.exists()) {
                transaction.set(counterRef, { 
                    nextOrderNumber: startOrderNumber + 1,
                    lastResetDate: todayStr 
                });
                return startOrderNumber;
            }

            const data = counterDoc.data();
            const lastResetDate = data.lastResetDate;
            let currentNumber = data.nextOrderNumber;

            if (lastResetDate !== todayStr) {
                // It's a new day, reset the counter
                currentNumber = startOrderNumber;
                transaction.update(counterRef, {
                    nextOrderNumber: startOrderNumber + 1,
                    lastResetDate: todayStr
                });
            } else {
                // It's the same day, just increment
                transaction.update(counterRef, { 
                    nextOrderNumber: currentNumber + 1 
                });
            }
            
            return currentNumber;
        });
        return newOrderNumber;
    } catch (e) {
        console.error("Transaction failed to get next order number: ", e);
        // Fallback to a non-sequential unique number in case of transaction failure
        // This is a safety net to prevent orders from failing completely.
        return Date.now() % 100000;
    }
}


export async function getOrderById(orderId: string): Promise<Order | null> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = doc(db, 'orders', orderId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return mapDocToOrder(docSnap);
    }
    return null;
}

export async function getOrdersByPostcode(storeId: string, postcode: string): Promise<Order[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const sanitizedPostcode = postcode.replace(/\s+/g, '').toUpperCase();
    if (sanitizedPostcode.length < 5) return []; // Basic validation

    const ordersRef = collection(db, 'orders');
    // This query requires a composite index on (storeId, deliveryDetails.sanitizedPostcode)
    const q = query(
        ordersRef,
        where('storeId', '==', storeId),
        where('deliveryDetails.sanitizedPostcode', '==', sanitizedPostcode)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
        return [];
    }

    const orders: Order[] = querySnapshot.docs.map(mapDocToOrder);

    // Deduplicate to show unique customers at that postcode
    const uniqueCustomers = new Map<string, Order>();
    orders.sort((a,b) => b.timestamp - a.timestamp).forEach(order => {
        if (!order.deliveryDetails) return;
        const key = `${order.deliveryDetails.customerName?.trim().toLowerCase()}-${order.deliveryDetails.addressLine1?.trim().toLowerCase()}`;
        if (!uniqueCustomers.has(key)) {
            uniqueCustomers.set(key, order);
        }
    });

    return Array.from(uniqueCustomers.values()).slice(0, 5); // Return max 5 suggestions
}

export async function getActiveOrders(storeId: string): Promise<Order[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(
        collection(db, 'orders'), 
        where('storeId', '==', storeId),
        where('status', 'in', ['pending', 'awaiting_payment', 'ready_for_pickup', 'out_for_delivery'])
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDocToOrder);
}

export async function getOrdersByDriverAndStatus(driverId: string, status: Order['status'], storeId: string): Promise<Order[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(
        collection(db, 'orders'),
        where('storeId', '==', storeId),
        where('driver.uid', '==', driverId),
        where('status', '==', status)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDocToOrder);
}

export async function batchUpdateOrderStatusAndPayment(updates: { orderId: string; paymentMethod: PaymentMethod }[]): Promise<void> {
    const currentDb = db;
    if (!currentDb) {
        throw new Error(DB_UNAVAILABLE_ERROR);
    }
    const batch = writeBatch(currentDb);
    updates.forEach(update => {
        if (update.paymentMethod) {
            const orderRef = doc(currentDb, 'orders', update.orderId);
            batch.update(orderRef, {
                status: 'completed',
                paymentMethod: update.paymentMethod,
                updatedAt: Timestamp.now(),
            });
        }
    });
    await batch.commit();
}

export async function getParkedOrders(storeId: string): Promise<Order[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(collection(db, 'orders'), where('storeId', '==', storeId), where('status', '==', 'parked'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(mapDocToOrder);
}

export async function saveOrder(orderData: Omit<Order, 'id' | 'timestamp' | 'updatedAt'>): Promise<Order> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const orderPayload: any = {
      ...orderData,
      timestamp: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    if (orderData.orderType === 'delivery' && orderData.deliveryDetails?.postcode) {
        orderPayload.deliveryDetails.sanitizedPostcode = orderData.deliveryDetails.postcode.replace(/\s+/g, '').toUpperCase();
    }
    
    const docRef = await addDoc(collection(db, 'orders'), orderPayload);
    const savedDoc = await getDoc(docRef);
    return mapDocToOrder(savedDoc);
}

export async function updateOrder(orderId: string, orderData: Partial<Omit<Order, 'id'|'timestamp'>>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const orderRef = doc(db, 'orders', orderId);
    const updatePayload: Record<string, any> = { ...orderData };
    
    // Ensure nested objects are handled correctly, especially for removing fields
    if (orderData.voidedBy === null) {
        updatePayload.voidedBy = null;
    }
    
    updatePayload.updatedAt = Timestamp.now();
    await updateDoc(orderRef, updatePayload);
}

export async function deleteOrder(orderId: string): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    await deleteDoc(doc(db, 'orders', orderId));
}

export async function updateOrderItemStatus(orderId: string, itemId: string): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const order = await getOrderById(orderId);
    if (order) {
        const items: OrderItem[] = order.items.map(item => {
            if (item.id === itemId) {
                return { ...item, status: item.status === 'pending' ? 'ready' : 'pending' };
            }
            return item;
        });
        await updateDoc(doc(db, 'orders', orderId), { items, updatedAt: Timestamp.now() });
    }
}

export async function assignOrderToTable(
    orderData: Omit<Order, 'id' | 'timestamp' | 'updatedAt'>,
    tableId: string,
    existingOrderId?: string | null
): Promise<Order> {
    const currentDb = db;
    if (!currentDb) {
        throw new Error(DB_UNAVAILABLE_ERROR);
    }
    const batch = writeBatch(currentDb);

    const orderRef = existingOrderId ? doc(currentDb, 'orders', existingOrderId) : doc(collection(currentDb, 'orders'));
    
    const isNewOrder = !existingOrderId;
    const finalOrderPayload: any = {
      ...orderData,
      updatedAt: Timestamp.now(),
    };
    
    if (isNewOrder) {
      finalOrderPayload.timestamp = Timestamp.now();
    }

    if (isNewOrder) {
        batch.set(orderRef, finalOrderPayload);
    } else {
        batch.update(orderRef, finalOrderPayload);
    }

    const tableRef = doc(currentDb, 'tables', tableId);
    batch.update(tableRef, {
        status: 'Occupied',
        currentOrderId: orderRef.id,
        currentOrderNumber: orderData.orderNumber,
        currentOrderTotal: orderData.total,
        currentOrderTimestamp: finalOrderPayload.updatedAt.toMillis(),
    });

    await batch.commit();
    
    const savedDoc = await getDoc(orderRef);
    return mapDocToOrder(savedDoc);
}


// --- Reports ---
export async function getSalesSummaryForDateRange(startDate: Date, endDate: Date, storeId: string) {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(
        collection(db, 'orders'), 
        where('storeId', '==', storeId),
        where('status', 'in', ['completed', 'ready_for_pickup', 'out_for_delivery']),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate))
    );
    const querySnapshot = await getDocs(q);
    
    const relevantOrders: Order[] = querySnapshot.docs.map(mapDocToOrder);

    return relevantOrders.reduce((acc, order) => {
        acc.totalRevenue += order.total;
        acc.totalOrders += 1;
        acc.totalItemsSold += order.items.reduce((itemSum, item) => itemSum + item.quantity, 0);
        if (order.paymentMethod === 'cash') acc.cashSales += order.total;
        else if (order.paymentMethod === 'card' || order.paymentMethod === 'mobile') acc.cardSales += order.total;
        else acc.otherSales += order.total;
        return acc;
      }, { totalRevenue: 0, totalOrders: 0, totalItemsSold: 0, cashSales: 0, cardSales: 0, otherSales: 0 });
}

export async function getSalesByCategoryForDateRange(startDate: Date, endDate: Date, storeId: string) {
  if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
  const categorySales: { [key: string]: number } = {};
  const ordersQuery = query(
      collection(db, 'orders'), 
      where('storeId', '==', storeId),
      where('status', 'in', ['completed', 'ready_for_pickup', 'out_for_delivery']),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate))
  );
  const ordersSnapshot = await getDocs(ordersQuery);
  const menuItemsSnapshot = await getDocs(query(collection(db, 'menuItems'), where('storeId', '==', storeId)));
  const menuItems = menuItemsSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()})) as MenuItem[];

  ordersSnapshot.docs.forEach(orderDoc => {
      const order = mapDocToOrder(orderDoc);
      order.items.forEach(item => {
          const menuItem = menuItems.find(mi => mi.id === item.menuItemId);
          if (menuItem) {
              if(!categorySales[menuItem.category]) categorySales[menuItem.category] = 0;
              categorySales[menuItem.category] += item.totalPrice;
          }
      });
  });

  return Object.entries(categorySales).map(([category, revenue]) => ({ category, revenue }));
}

export async function getSalesByItemForDateRange(startDate: Date, endDate: Date, storeId: string) {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const itemSales: { [key: string]: { id: string; name: string; quantitySold: number; revenue: number } } = {};
    const ordersQuery = query(
        collection(db, 'orders'), 
        where('storeId', '==', storeId),
        where('status', 'in', ['completed', 'ready_for_pickup', 'out_for_delivery']),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate))
    );
    const ordersSnapshot = await getDocs(ordersQuery);
    
    ordersSnapshot.docs.forEach(orderDoc => {
        const order = mapDocToOrder(orderDoc);
        order.items.forEach(item => {
            if (!itemSales[item.menuItemId]) {
                itemSales[item.menuItemId] = { id: item.menuItemId, name: item.name, quantitySold: 0, revenue: 0 };
            }
            itemSales[item.menuItemId].quantitySold += item.quantity;
            itemSales[item.menuItemId].revenue += item.totalPrice;
        });
    });

    return Object.values(itemSales).sort((a,b) => b.revenue - a.revenue);
}

// --- Cash Up ---
export async function addCashUpSession(sessionData: Omit<CashUpSession, 'id' | 'finalizedAt'>): Promise<CashUpSession> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const sessionPayload = { ...sessionData, finalizedAt: Timestamp.now() };
    const docRef = await addDoc(collection(db, 'cashUpSessions'), sessionPayload);
    const savedDoc = await getDoc(docRef);
    const savedData = savedDoc.data()!;
    return { 
        id: docRef.id, 
        finalizedAt: savedData.finalizedAt.toMillis(),
        storeId: savedData.storeId,
        date: savedData.date,
        startingFloat: savedData.startingFloat,
        cashDenominations: savedData.cashDenominations,
        totalCashCounted: savedData.totalCashCounted,
        payouts: savedData.payouts,
        totalPayouts: savedData.totalPayouts,
        reportedCashSalesPOS: savedData.reportedCashSalesPOS,
        reportedCardSales: savedData.reportedCardSales,
        reportedOtherSales: savedData.reportedOtherSales,
        totalReportedTakings: savedData.totalReportedTakings,
        netCashInTillActual: savedData.netCashInTillActual,
        expectedCashInTill: savedData.expectedCashInTill,
        difference: savedData.difference,
        notes: savedData.notes,
    };
}

export async function getCashUpSessionForDate(date: Date, storeId: string): Promise<CashUpSession | null> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const targetDateStr = date.toISOString().split('T')[0]; // Get YYYY-MM-DD part

    const q = query(collection(db, 'cashUpSessions'), where('storeId', '==', storeId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
        return null;
    }
    
    // Find a session where the date string (e.g., "2024-07-25T...") starts with the target date string
    const matchingDoc = querySnapshot.docs.find(doc => {
        const docDate = doc.data().date;
        return typeof docDate === 'string' && docDate.startsWith(targetDateStr);
    });

    if (!matchingDoc) {
        return null;
    }

    const docData = matchingDoc.data();
    return { 
        id: matchingDoc.id, 
        date: docData.date,
        finalizedAt: (docData.finalizedAt?.toMillis ? docData.finalizedAt.toMillis() : docData.finalizedAt) ?? 0,
        storeId: docData.storeId,
        startingFloat: docData.startingFloat || 0,
        cashDenominations: docData.cashDenominations || [],
        totalCashCounted: docData.totalCashCounted || 0,
        payouts: docData.payouts || [],
        totalPayouts: docData.totalPayouts || 0,
        reportedCashSalesPOS: docData.reportedCashSalesPOS || 0,
        reportedCardSales: docData.reportedCardSales || 0,
        reportedOtherSales: docData.reportedOtherSales || 0,
        totalReportedTakings: docData.totalReportedTakings || 0,
        netCashInTillActual: docData.netCashInTillActual || 0,
        expectedCashInTill: docData.expectedCashInTill || 0,
        difference: docData.difference || 0,
        notes: docData.notes || '',
    };
}

export async function getLatestFinalizedCashUpSession(beforeDate: Date, storeId: string): Promise<CashUpSession | null> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const q = query(
        collection(db, 'cashUpSessions'),
        where('storeId', '==', storeId),
        where('date', '<', beforeDate.toISOString()),
        orderBy('date', 'desc'),
        limit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
        return null;
    }

    const doc = querySnapshot.docs[0];
    const docData = doc.data();
    return { 
        id: doc.id, 
        date: docData.date,
        finalizedAt: (docData.finalizedAt?.toMillis ? docData.finalizedAt.toMillis() : docData.finalizedAt) ?? 0,
        storeId: docData.storeId,
        startingFloat: docData.startingFloat || 0,
        cashDenominations: docData.cashDenominations || [],
        totalCashCounted: docData.totalCashCounted || 0,
        payouts: docData.payouts || [],
        totalPayouts: docData.totalPayouts || 0,
        reportedCashSalesPOS: docData.reportedCashSalesPOS || 0,
        reportedCardSales: docData.reportedCardSales || 0,
        reportedOtherSales: docData.reportedOtherSales || 0,
        totalReportedTakings: docData.totalReportedTakings || 0,
        netCashInTillActual: docData.netCashInTillActual || 0,
        expectedCashInTill: docData.expectedCashInTill || 0,
        difference: docData.difference || 0,
        notes: docData.notes || '',
    };
}


// --- Shop Settings (Firestore) ---
export async function getShopSettings(storeId: string): Promise<ShopInfoState> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = doc(db, 'shopSettings', storeId);
    const docSnap = await getDoc(docRef);

    const defaults = createDefaultShopInfoState();
    const dbData = docSnap.exists() ? docSnap.data() : {};
    
    // Explicitly check if the nested settings are objects before spreading
    const clientSettingsFromDb = typeof dbData.clientReceiptSettings === 'object' && dbData.clientReceiptSettings !== null 
        ? dbData.clientReceiptSettings 
        : {};

    const kitchenSettingsFromDb = typeof dbData.kitchenReceiptSettings === 'object' && dbData.kitchenReceiptSettings !== null 
        ? dbData.kitchenReceiptSettings 
        : {};

    const settings: ShopInfoState = {
        id: storeId,
        storeId: storeId,
        shopName: dbData.shopName ?? defaults.shopName,
        addressStreet: dbData.addressStreet ?? defaults.addressStreet,
        addressCity: dbData.addressCity ?? defaults.addressCity,
        addressPostcode: dbData.addressPostcode ?? defaults.addressPostcode,
        addressCountry: dbData.addressCountry ?? defaults.addressCountry,
        contactPhone: dbData.contactPhone ?? defaults.contactPhone,
        contactEmail: dbData.contactEmail ?? defaults.contactEmail,
        website: dbData.website ?? defaults.website,
        taxId: dbData.taxId ?? defaults.taxId,
        taxRate: dbData.taxRate ?? defaults.taxRate,
        pricesIncludeTax: dbData.pricesIncludeTax ?? defaults.pricesIncludeTax,
        receiptHeader: dbData.receiptHeader ?? defaults.receiptHeader,
        receiptFooter: dbData.receiptFooter ?? defaults.receiptFooter,
        showLogoOnReceipt: dbData.showLogoOnReceipt ?? defaults.showLogoOnReceipt,
        currencySymbol: dbData.currencySymbol ?? defaults.currencySymbol,
        currencyCode: dbData.currencyCode ?? defaults.currencyCode,
        openingHours: dbData.openingHours ?? defaults.openingHours,
        exitKioskPin: dbData.exitKioskPin ?? defaults.exitKioskPin,
        kioskAllowedPaymentMethods: dbData.kioskAllowedPaymentMethods ?? defaults.kioskAllowedPaymentMethods,
        minimumDeliveryOrderValue: dbData.minimumDeliveryOrderValue ?? defaults.minimumDeliveryOrderValue,
        deliveryZones: dbData.deliveryZones ?? defaults.deliveryZones,
        storeLatitude: dbData.storeLatitude ?? defaults.storeLatitude,
        storeLongitude: dbData.storeLongitude ?? defaults.storeLongitude,
        clientReceiptSettings: {
            ...defaults.clientReceiptSettings,
            ...clientSettingsFromDb,
        },
        kitchenReceiptSettings: {
            ...defaults.kitchenReceiptSettings,
            ...kitchenSettingsFromDb,
        },
    };

    return settings;
}


export async function saveShopSettings(storeId: string, settings: Omit<ShopInfoState, 'id' | 'storeId'>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const docRef = doc(db, 'shopSettings', storeId);
    await setDoc(docRef, settings, { merge: true });
}


// --- New User Profile & Store Functions ---

export async function createUserProfile(userId: string, data: Omit<AppUser, 'uid'>): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { uid: userId, ...data });
}

export async function getUserProfile(userId: string): Promise<AppUser | null> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const userRef = doc(db, 'users', userId);
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            uid: data.uid,
            email: data.email || null,
            fullName: data.fullName || null,
            accessibleStores: data.accessibleStores || {},
            pin: data.pin || '',
        };
    }
    return null;
}

export async function createStore(userId: string, storeName: string): Promise<Store> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const storeData = {
        name: storeName,
        ownerId: userId,
    };
    const storeRef = await addDoc(collection(db, 'stores'), storeData);
    
    // Grant owner access to this new store
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
        [`accessibleStores.${storeRef.id}`]: 'Admin'
    });
    
    return { id: storeRef.id, ...storeData };
}

export async function getStoresForUser(storeIds: string[]): Promise<Store[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    if (storeIds.length === 0) return [];
    const storesRef = collection(db, 'stores');
    const q = query(storesRef, where(documentId(), 'in', storeIds));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
            id: doc.id,
            name: data.name || '',
            ownerId: data.ownerId || '',
        };
    });
}

export async function getAllStores(): Promise<Store[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const storesRef = collection(db, 'stores');
    const querySnapshot = await getDocs(storesRef);
    return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
            id: doc.id,
            name: data.name || '',
            ownerId: data.ownerId || '',
        };
    }).sort((a,b) => a.name.localeCompare(b.name));
}


// --- New User Management Functions for Stores ---

export async function getUsersForStore(storeId: string): Promise<AppUser[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where(`accessibleStores.${storeId}`, 'in', ['Admin', 'Manager', 'Staff', 'Driver']));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AppUser);
}

export async function getUsersByRole(role: UserRole, storeId: string): Promise<AppUser[]> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where(`accessibleStores.${storeId}`, '==', role));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => doc.data() as AppUser);
}

export async function assignUserToStoreByEmail({ email, storeId, role, pin, fullName }: { email: string; storeId: string; role: UserRole; pin: string; fullName?: string }): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where("email", "==", email));
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
        throw new Error(`No user found with the email: ${email}. Please ensure the user has signed up first.`);
    }

    const userDoc = querySnapshot.docs[0];
    const userRef = doc(db, 'users', userDoc.id);

    const updatePayload: Record<string, any> = {
        [`accessibleStores.${storeId}`]: role,
        pin: pin,
    };
    
    // Only update name if it's provided and different
    if (fullName && fullName.trim() !== '' && userDoc.data().fullName !== fullName.trim()) {
        updatePayload.fullName = fullName.trim();
    }

    await updateDoc(userRef, updatePayload);
}

export async function removeUserFromStore(userId: string, storeId: string): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const userRef = doc(db, 'users', userId);
    const userProfile = await getUserProfile(userId);
    if(userProfile && userProfile.accessibleStores[storeId]) {
        delete userProfile.accessibleStores[storeId];
        await updateDoc(userRef, {
            accessibleStores: userProfile.accessibleStores
        });
    } else {
        throw new Error("User does not have access to this store or user not found.");
    }
}

export async function updateUserDetails(userId: string, storeId: string, data: { fullName?: string, pin?: string, role?: UserRole }): Promise<void> {
    if (!db) throw new Error(DB_UNAVAILABLE_ERROR);
    const userRef = doc(db, 'users', userId);
    const updatePayload: any = {};

    if (data.fullName !== undefined) {
        updatePayload.fullName = data.fullName;
    }
    if (data.pin !== undefined) {
        updatePayload.pin = data.pin;
    }
    if (data.role !== undefined) {
        updatePayload[`accessibleStores.${storeId}`] = data.role;
    }
    
    if (Object.keys(updatePayload).length > 0) {
        await updateDoc(userRef, updatePayload);
    }
}
